import React, { useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faInfoCircle,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";
import ReactGA from "react-ga4";
import moment from "moment";
import { useCookies } from "react-cookie";
import { useContainer } from "unstated-next";
import { usePostHog } from "posthog-js/react";

import Tooltip from "../../../components/Tooltip";
import GoogleAutocomplete from "../../../components/GoogleAutocomplete";
import { GlobalStateContainer } from "../../../provider/GlobalStateContainer";
import i18n from "../../../i18n/i18n";

import auth from "../../../utils/auth";
import { Routes } from "../../../utils/routes";
import {
  AuthModalMode,
  RentParkingNearbyType,
} from "../../../utils/constants";
import { cleanUndefined } from "../../../utils/stringFormatter";
import useToasts from "../../../hooks/useToasts";
import {
  createUserVehicle,
  getAdminFees,
  getUserVehicles,
  rentParking,
  sendEnquiryNowNotification,
} from "../../../services/apiService";

const RentParkingForm = ({ parkingArea }) => {
  const { authedUser, setAuthModalState } = useContainer(GlobalStateContainer);
  const authenticated = Boolean(auth.getRefreshToken());
  const { toastError, toastSuccess } = useToasts();
  const posthog = usePostHog();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [cookies, setCookie] = useCookies([
    parkingArea.id,
    "generalParkingAffiliate",
  ]);
  const [startDate, setStartDate] = useState();
  const [nearbyType, setNearbyType] = useState(RentParkingNearbyType.NONE);
  const [nearbyAddress, setNearbyAddress] = useState();
  const [nearbyLocation, setNearbyLocation] = useState();
  const [vehiclesList, setVehiclesList] = useState([]);
  const [vehicleId, setVehicleId] = useState();
  const [isNewVehicle, setIsNewVehicle] = useState(true);
  const [adminFees, setAdminFees] = useState(0);
  const [newVehicle, setNewVehicle] = useState({
    model: "",
    color: "",
    registrationNumber: "",
  });

  useEffect(() => {
    const handleGetVehicles = async () => {
      const response = await getUserVehicles();
      if (response.data) {
        setVehiclesList(response.data);
        if (response.data.length > 0) {
          setIsNewVehicle(false);
        }
      }
    };

    if (authedUser) {
      handleGetVehicles();
    }
  }, [authedUser]);

  useEffect(() => {
    const handleGetAdminFees = async (currency) => {
      const response = await getAdminFees(currency);
      console.log(response.data);
      if (response.data) {
        setAdminFees(response.data);
      }
    };

    if (parkingArea) {
      handleGetAdminFees(parkingArea.currency);
    }
  }, [parkingArea]);

  const handleSubmit = async (bookNow) => {
    setSubmitting(true);
    if (!startDate) {
      toastError("Please select a start date!");
      setSubmitting(false);
      return;
    }

    if (!vehicleId && !isNewVehicle) {
      toastError("Please select a vehicle!");
      setSubmitting(false);
      return;
    }

    if (isNewVehicle) {
      const { model, color, registrationNumber } = newVehicle;
      if (!model || !color || !registrationNumber) {
        toastError("Please provide vehicle details");
        setSubmitting(false);
        return;
      }
    }

    // format startDate
    let formattedStartDate = moment(startDate).format("YYYY-MM-DD");

    if (!authenticated) {
      setAuthModalState({ isModalOpen: true, mode: AuthModalMode.LOGIN });
      setSubmitting(false);
    } else {
      ReactGA.event({
        category: "Renting",
        action: "User request renting",
      });

      posthog?.capture(bookNow ? "book_now" : "enquire_now", {
        parking_area_id: parkingArea.id,
        parking_area_name: parkingArea.name,
      });

      // Google Ads Tracking
      if (!bookNow) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "enquire_now_complete",
          enhanced_conversion_data: {
            email: authedUser.userName,
          },
        });

        const array = window.dataLayer;
        // eslint-disable-next-line array-callback-return
        var indX = array.findIndex(function (post, index) {
          if (post.event === "enquire_now_complete") return true;
        });

        var ecEmail = array[indX].enhanced_conversion_data.email;

        // eslint-disable-next-line no-unused-vars
        var enhanced_conversion_data = {
          email: ecEmail,
        };

        window.gtag("event", "conversion", {
          send_to: "AW-803504205/IF9nCLyl8pECEM2Akv8C",
        });
      }

      const affiliatePartnerId = cleanUndefined(cookies[parkingArea.id]);
      const generalAffiliatePartnerId = cleanUndefined(
        cookies["generalParkingAffiliate"]
      );

      try {
        let parkingPayload = {
          requestedParkingAreaId: parkingArea.id,
          startDate: formattedStartDate,
          additionalInformation: {
            nearbyType,
            nearbyAddress,
            nearbyLocation,
          },
          ...(affiliatePartnerId && {
            affiliateCampaignParkingAreaId: parkingArea.id,
          }),
          ...((affiliatePartnerId || generalAffiliatePartnerId) && {
            affiliateCampaignId:
              affiliatePartnerId ?? generalAffiliatePartnerId,
          }),
        };

        var response;
        if (isNewVehicle) {
          const vehicleResponse = await createUserVehicle(newVehicle);

          parkingPayload = {
            ...parkingPayload,
            vehicle: { id: vehicleResponse.data.id },
          };

          response = await rentParking(parkingPayload);
        } else {
          parkingPayload = { ...parkingPayload, vehicle: { id: vehicleId } };
          response = await rentParking(parkingPayload);
        }

        // Remove cookie
        setCookie(parkingArea.id, null);
        setCookie("generalParkingAffiliate", null);

        if (bookNow) {
          navigate(`/dashboard/rented-parking-detail/${response.data.id}`, {
            replace: true,
          });
        } else {
          await sendEnquiryNowNotification(response.data.id);
          toastSuccess(i18n.t("findParking.enquireSuccess"));
          navigate(Routes.RentedParking, { replace: true });
        }
      } catch (error) {
        if (isAxiosError(error)) {
          toastError(error.response.data.message);
        } else {
          toastError(error.message);
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  const formatCurrency = (currency) => {
    switch (currency) {
      case "MYR":
        return "RM";
      case "SGD":
        return "S$";
      case "HKD":
        return "HK$";
      default:
        return "RM";
    }
  };

  const getCurrency = () => {
    let currency = "RM";

    if (parkingArea && parkingArea.currency) {
      return formatCurrency(parkingArea.currency);
    }

    return currency;
  };

  return (
    <div className="flex flex-col gap-[15px]">
      <div className="flex flex-col gap-[7px]">
        <span className="text-[12px]">
          {i18n.t("findParking.startDate")}
          <span className="text-red-600 ml-1">*</span>
        </span>
        <DatePicker
          selected={startDate}
          value={startDate}
          minDate={new Date()}
          onChange={(date) => setStartDate(date)}
          placeholderText="mm/dd/yyyy"
          autoComplete="off"
          className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
          required
        />
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex justify-between gap-3 pr-2">
          <span className="text-[12px]">
            {i18n.t("findParking.selectVehicle")}
            <span className="text-red-600 ml-1">*</span>
          </span>
          {vehiclesList.length > 0 && (
            <button onClick={() => setIsNewVehicle(false)}>
              <FontAwesomeIcon icon={faCar} className="text-primary900" />
            </button>
          )}
        </div>
        {isNewVehicle ? (
          <>
            <input
              required
              type="text"
              onChange={(e) =>
                setNewVehicle({ ...newVehicle, model: e.target.value })
              }
              value={newVehicle.model}
              placeholder="Model i.e. Honda City"
              className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
            />
            <input
              required
              type="text"
              onChange={(e) =>
                setNewVehicle({
                  ...newVehicle,
                  registrationNumber: e.target.value,
                })
              }
              value={newVehicle.registrationNumber}
              placeholder="Number Plate i.e. ABC1234"
              className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
            />
            <input
              required
              type="text"
              onChange={(e) =>
                setNewVehicle({ ...newVehicle, color: e.target.value })
              }
              value={newVehicle.color}
              placeholder="Color"
              className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
            />
          </>
        ) : (
          <div className="flex justify-between gap-3">
            <select
              onChange={(e) => setVehicleId(e.target.value)}
              className="w-full rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
            >
              <option>{i18n.t("findParking.selectVehicle")}</option>
              {vehiclesList.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.registrationNumber} - {vehicle.model} {vehicle.color}
                </option>
              ))}
            </select>
            <button onClick={() => setIsNewVehicle(true)}>
              <FontAwesomeIcon
                icon={faPlusCircle}
                className="text-primary900"
              />
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-[7px]">
        <ul className="flex flex-wrap sm:flex-nowrap items-center w-full border border-[#ccc] rounded-[10px]">
          <li className="w-full border-[#ccc] border-b sm:border-b-0 sm:border-r">
            <div className="flex items-center pl-3">
              <input
                id="stay-radio"
                type="radio"
                checked={nearbyType === RentParkingNearbyType.STAY_HERE}
                onChange={(e) =>
                  e.target.checked &&
                  setNearbyType(RentParkingNearbyType.STAY_HERE)
                }
                className="w-4 h-4 text-primary900 bg-gray-100 border-gray-300 focus:ring-0"
              />
              <label
                htmlFor="stay-radio"
                className="w-full py-3 ml-2 text-[12px] font-medium"
              >
                {i18n.t("findParking.stayingHere")}{" "}
              </label>
            </div>
          </li>
          <li className="w-full border-[#ccc] border-b sm:border-b-0 sm:border-r">
            <div className="flex items-center pl-3">
              <input
                id="work-radio"
                type="radio"
                checked={nearbyType === RentParkingNearbyType.WORK_HERE}
                onChange={(e) =>
                  e.target.checked &&
                  setNearbyType(RentParkingNearbyType.WORK_HERE)
                }
                className="w-4 h-4 text-primary900 bg-gray-100 border-gray-300 focus:ring-0"
              />
              <label
                htmlFor="work-radio"
                className="w-full py-3 ml-2 text-[12px] font-medium"
              >
                {i18n.t("findParking.workingHere")}
              </label>
            </div>
          </li>
          <li className="w-full">
            <div className="flex items-center pl-3">
              <input
                id="staywork-radio"
                type="radio"
                checked={
                  nearbyType === RentParkingNearbyType.STAY_OR_WORK_NEARBY
                }
                onChange={(e) =>
                  e.target.checked &&
                  setNearbyType(RentParkingNearbyType.STAY_OR_WORK_NEARBY)
                }
                className="w-4 h-4 text-primary900 bg-gray-100 border-gray-300 focus:ring-0"
              />
              <label
                htmlFor="staywork-radio"
                className="w-full py-3 ml-2 text-[12px] font-medium"
              >
                {i18n.t("findParking.stayingOrWorkingNearby")}
              </label>
            </div>
          </li>
        </ul>
      </div>
      <div className="flex flex-col gap-[7px]">
        <span className="text-[12px]">
          {i18n.t("findParking.nearbyAddress")}
        </span>
        <div className="flex items-center gap-[7px]">
          <GoogleAutocomplete
            placeholder={i18n.t("findParking.nearbyAddress")}
            onPlaceChanged={(place) => {
              setNearbyLocation({
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
              });
              setNearbyAddress(
                `${place.name}${
                  place.formatted_address ? ", " + place.formatted_address : ""
                }`
              );
            }}
            className="w-full h-[42px] rounded-[10px] border border-[#ccc] text-[12px] focus:border-none focus:ring-primary900"
          />
          <Tooltip
            placement="top"
            trigger="hover"
            tooltip={i18n.t("findParking.exactBuilding")}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="text-[#ccc]" />
          </Tooltip>
        </div>
      </div>
      {adminFees > 0 && (
        <div className="flex flex-col gap-[7px]">
          <span className="text-[12px]">{i18n.t("findParking.adminFeeTooltip.adminFee")}</span>
          <div className="flex items-center gap-[7px]">
            <div className="border border-[#ccc] text-sm p-2 rounded-lg w-full">
              {getCurrency()} {adminFees / 100}
            </div>
            <Tooltip
              placement="top"
              trigger="hover"
              tooltip={
                <div>
                  <b>{i18n.t('findParking.adminFeeTooltip.adminFee')}</b>
                  <p>{i18n.t('findParking.adminFeeTooltip.adminFeeDescription')}</p>
                  <br />
                  <b>{i18n.t('findParking.adminFeeTooltip.waitingList')}</b>
                  <p>{i18n.t('findParking.adminFeeTooltip.waitingListDescription')}</p>
                </div>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="text-[#ccc]" />
            </Tooltip>
          </div>
        </div>
      )}
      <div className="flex mt-4 pb-5 gap-[10px] items-center flex-row justify-center w-full flex-wrap">
        <button
          onClick={() => handleSubmit(true)}
          disabled={submitting}
          className="bg-primary900 text-[11px] text-white w-[150px] rounded-[12px] font-medium py-[10px] sm:text-[13px] sm:rounded-[18px] disabled:opacity-50"
        >
          {i18n.t("findParking.bookNow")}
        </button>
        <div className="flex flex-row items-center">
          <button
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="border border-primary900 mr-[8px] text-[11px] text-primary900 w-[126px] rounded-[12px] font-medium py-[10px] sm:w-[150px] sm:mr-[12px] sm:text-[13px] sm:rounded-[18px] disabled:opacity-50"
          >
            {i18n.t("findParking.enquireNow")}
          </button>
          <Tooltip
            placement="top"
            trigger="hover"
            tooltip={i18n.t("findParking.supportTeam")}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="text-[#ccc]" />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default RentParkingForm;
